import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, IconButton,
  Tooltip, Divider,
} from '@mui/material';
import { ArrowBack, Payments, PostAdd, Print, Download, Edit, Block, CheckCircle, Refresh } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

// Customer Accounts (accounts receivable): what each customer was invoiced, what they actually paid,
// and the running balance. Payments clear the oldest invoice first; any excess is credit on account.

const money = (v: any, cur = 'USD') => (v === null || v === undefined) ? '—'
  : `${Number(v) < 0 ? '-' : ''}${cur} ${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const amt = (v: any) => Number(v) ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
const day = (v: any) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const today = () => new Date().toISOString().slice(0, 10);
const STATE: Record<string, { label: string; color: any }> = {
  paid: { label: 'Paid', color: 'success' }, part_paid: { label: 'Part paid', color: 'warning' },
  overdue: { label: 'Overdue', color: 'error' }, open: { label: 'Open', color: 'default' },
};
const METHODS = ['bank_transfer', 'cash', 'cheque', 'card', 'other'];
const FINANCE = ['super_admin', 'billing_admin', 'provider_admin'];

export const CustomerAccountsPage: React.FC = () => {
  const role = useAuthStore(s => s.user?.role);
  const { enqueueSnackbar } = useSnackbar();
  const [list, setList] = useState<any[]>([]);
  const [sel, setSel] = useState<string>(() => new URLSearchParams(window.location.search).get('customer') || '');
  const [st, setSt] = useState<any>(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [pay, setPay] = useState<any>(null);
  const [adj, setAdj] = useState<any>(null);
  const [voiding, setVoiding] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(() => {
    api.get('/accounts').then(r => setList(r.data)).catch(() => setList([]));
  }, []);
  const loadSt = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get(`/accounts/${id}/statement`, { params: { from: range.from || undefined, to: range.to || undefined } });
      setSt(r.data);
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed to load statement', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [range, enqueueSnackbar]);

  useEffect(() => { if (FINANCE.includes(role || '')) loadList(); }, [role, loadList]);
  useEffect(() => { if (sel) loadSt(sel); else setSt(null); }, [sel, loadSt]);

  if (!FINANCE.includes(role || '')) return <Alert severity="warning">Customer Accounts is available to finance roles only.</Alert>;

  const open = (id: string) => { setSel(id); window.history.replaceState(null, '', id ? `?customer=${id}` : window.location.pathname); };
  const refresh = () => { loadList(); if (sel) loadSt(sel); };
  const cur = st?.currency || 'USD';

  const savePayment = async () => {
    setBusy(true);
    try {
      const body = { amount: Number(pay.amount), received_at: pay.received_at, method: pay.method, reference: pay.reference,
                     notes: pay.notes, bank_charges: Number(pay.bank_charges || 0), currency: cur };
      if (pay.id) await api.put(`/accounts/payments/${pay.id}`, { ...body, confirm: pay.confirm === true });
      else await api.post(`/accounts/${sel}/payments`, body);
      enqueueSnackbar(pay.id ? 'Payment updated' : 'Payment recorded', { variant: 'success' });
      setPay(null); refresh();
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Could not save payment', { variant: 'error' }); }
    finally { setBusy(false); }
  };
  const saveAdj = async () => {
    setBusy(true);
    try {
      await api.post(`/accounts/${sel}/adjustments`, { kind: adj.kind, amount: Number(adj.amount), adj_date: adj.adj_date, reason: adj.reason });
      enqueueSnackbar('Adjustment added', { variant: 'success' }); setAdj(null); refresh();
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Could not add adjustment', { variant: 'error' }); }
    finally { setBusy(false); }
  };
  const doVoid = async () => {
    setBusy(true);
    try {
      await api.delete(`/accounts/${voiding.type === 'adjustment' ? 'adjustments' : 'payments'}/${voiding.id}`, { data: { reason: voiding.reason } });
      enqueueSnackbar('Voided', { variant: 'info' }); setVoiding(null); refresh();
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Could not void', { variant: 'error' }); }
    finally { setBusy(false); }
  };
  const exportCsv = () => {
    if (!st) return;
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['Date', 'Type', 'Reference', 'Description', 'Debit', 'Credit', 'Balance']]
      .concat(st.lines.map((l: any) => [new Date(l.date).toISOString().slice(0, 10), l.type, l.reference, l.description, l.debit || '', l.credit || '', l.balance]));
    const url = URL.createObjectURL(new Blob([rows.map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `statement-${st.customer.name}-${today()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  // ── Customer list ─────────────────────────────────────────────────────────
  if (!sel) {
    const tot = list.reduce((a, c) => ({ due: a.due + c.balance_due, overdue: a.overdue + c.overdue, credit: a.credit + c.credit_on_account }), { due: 0, overdue: 0, credit: 0 });
    return (
      <Box>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight={700}>Customer Accounts</Typography>
            <Typography variant="body2" color="text.secondary">Invoiced vs actually received, and what each customer owes. Payments clear the oldest invoice first; any excess is held as credit.</Typography>
          </Box>
          <Button startIcon={<Refresh />} onClick={loadList}>Refresh</Button>
        </Stack>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {[['Total outstanding', tot.due, 'text.primary'], ['Overdue', tot.overdue, 'error.main'], ['Credit held for customers', tot.credit, 'success.main']].map(([l, v, c]: any) => (
            <Card key={l} variant="outlined" sx={{ flex: '1 1 200px' }}><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{l}</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: c }}>{money(v)}</Typography>
            </CardContent></Card>
          ))}
        </Stack>
        <Card variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Customer</TableCell><TableCell align="right">Invoiced</TableCell><TableCell align="right">Received</TableCell>
              <TableCell align="right">Balance due</TableCell><TableCell align="right">Overdue</TableCell><TableCell align="right">Credit</TableCell>
              <TableCell>Last payment</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {list.map(c => (
                <TableRow key={c.tenant_id} hover sx={{ cursor: 'pointer' }} onClick={() => open(c.tenant_id)}>
                  <TableCell><Typography fontWeight={600}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.billing_type} · {c.open_invoices} open invoice{c.open_invoices === 1 ? '' : 's'}</Typography></TableCell>
                  <TableCell align="right">{money(c.invoiced, c.currency)}</TableCell>
                  <TableCell align="right">{money(c.received, c.currency)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{money(c.balance_due, c.currency)}</TableCell>
                  <TableCell align="right" sx={{ color: c.overdue > 0 ? 'error.main' : undefined }}>{money(c.overdue, c.currency)}</TableCell>
                  <TableCell align="right" sx={{ color: c.credit_on_account > 0 ? 'success.main' : undefined }}>{money(c.credit_on_account, c.currency)}</TableCell>
                  <TableCell>{c.last_payment ? `${day(c.last_payment.date)} · ${money(c.last_payment.amount, c.currency)}` : '—'}
                    {c.pending_payments > 0 && <Chip size="small" color="warning" label={`${c.pending_payments} to confirm`} sx={{ ml: 1 }} />}</TableCell>
                  <TableCell align="right"><Button size="small">Statement</Button></TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No customer invoices or payments yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </Box>
    );
  }

  // ── Statement ─────────────────────────────────────────────────────────────
  return (
    <Box>
      <style>{`@media print { nav, header, .MuiAppBar-root, .MuiDrawer-root, .no-print { display: none !important; } main { margin: 0 !important; padding: 0 !important; width: 100% !important; } }`}</style>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <IconButton className="no-print" onClick={() => open('')}><ArrowBack /></IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>Statement of account — {st?.customer?.name || '…'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {range.from || range.to ? `${range.from ? day(range.from) : 'Start'} to ${range.to ? day(range.to) : 'today'}` : 'All activity'} · printed {day(new Date())}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} className="no-print" useFlexGap flexWrap="wrap">
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} />
          <Button variant="contained" startIcon={<Payments />} onClick={() => setPay({ amount: '', received_at: today(), method: 'bank_transfer', reference: '', notes: '', bank_charges: '' })}>Record payment</Button>
          <Button startIcon={<PostAdd />} onClick={() => setAdj({ kind: 'credit', amount: '', adj_date: today(), reason: '' })}>Adjustment</Button>
          <Button startIcon={<Download />} onClick={exportCsv}>CSV</Button>
          <Button startIcon={<Print />} onClick={() => window.print()}>Print / PDF</Button>
        </Stack>
      </Stack>

      {loading && !st && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
      {st && (
        <>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
            {[[st.closing_balance >= 0 ? 'Balance due' : 'Credit on account', Math.abs(st.closing_balance), st.closing_balance > 0 ? 'error.main' : 'success.main'],
              ['Not yet due', st.aging.not_due], ['1–30 days overdue', st.aging.d1_30], ['31–60 days', st.aging.d31_60], ['61–90 days', st.aging.d61_90], ['Over 90 days', st.aging.d90_plus]]
              .map(([l, v, c]: any) => (
                <Card key={l} variant="outlined" sx={{ flex: '1 1 140px' }}><CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                  <Typography variant="caption" color="text.secondary">{l}</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: c }}>{money(v, cur)}</Typography>
                </CardContent></Card>
              ))}
          </Stack>

          {st.pending_payments?.length > 0 && (
            <Alert severity="warning" className="no-print" sx={{ mb: 2 }}>
              <b>Payments reported by the customer, not yet confirmed</b> (they don't count until you confirm the amount actually received):
              {st.pending_payments.map((p: any) => (
                <Box key={p.id} sx={{ mt: 0.5 }}>
                  {day(p.received_at)} · {money(p.amount, cur)} · {p.reference || 'no reference'}
                  <Button size="small" sx={{ ml: 1 }} startIcon={<CheckCircle />}
                    onClick={() => setPay({ id: p.id, confirm: true, amount: p.amount, received_at: String(p.received_at).slice(0, 10), method: p.method, reference: p.reference || '', notes: p.notes || '', bank_charges: '' })}>Confirm</Button>
                  <Button size="small" color="error" onClick={() => setVoiding({ type: 'payment', id: p.id, reason: '' })}>Reject</Button>
                </Box>
              ))}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Date</TableCell><TableCell>Details</TableCell>
                <TableCell align="right">Charges</TableCell><TableCell align="right">Payments / credits</TableCell><TableCell align="right">Balance</TableCell>
                <TableCell className="no-print" />
              </TableRow></TableHead>
              <TableBody>
                {(range.from) && <TableRow><TableCell>{day(range.from)}</TableCell><TableCell><i>Opening balance</i></TableCell><TableCell /><TableCell /><TableCell align="right">{amt(st.opening_balance) || '0.00'}</TableCell><TableCell className="no-print" /></TableRow>}
                {st.lines.map((l: any, i: number) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(l.date)}</TableCell>
                    <TableCell>{l.description}</TableCell>
                    <TableCell align="right">{amt(l.debit)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{amt(l.credit)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{Number(l.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="no-print" padding="checkbox" sx={{ whiteSpace: 'nowrap' }}>
                      {l.type === 'payment' && l.payment && <>
                        <Tooltip title="Edit payment"><IconButton size="small" onClick={() => setPay({
                          id: l.id, amount: Number(l.payment.amount), received_at: String(l.payment.received_at).slice(0, 10),
                          method: l.payment.method || 'bank_transfer', reference: l.payment.reference || '', notes: l.payment.notes || '',
                          bank_charges: Number(l.payment.bank_charges) || '' })}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Void payment"><IconButton size="small" color="error" onClick={() => setVoiding({ type: 'payment', id: l.id, reason: '' })}><Block fontSize="small" /></IconButton></Tooltip>
                      </>}
                      {l.type === 'adjustment' && <Tooltip title="Void adjustment"><IconButton size="small" color="error" onClick={() => setVoiding({ type: 'adjustment', id: l.id, reason: '' })}><Block fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: 2 } }}>
                  <TableCell /><TableCell>{st.closing_balance >= 0 ? 'Balance due' : 'Credit on account'}</TableCell><TableCell /><TableCell />
                  <TableCell align="right">{money(st.closing_balance, cur)}</TableCell><TableCell className="no-print" />
                </TableRow>
              </TableBody>
            </Table>
          </Card>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Invoices</Typography>
          <Card variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Invoice</TableCell><TableCell>Period</TableCell><TableCell>Issued</TableCell><TableCell>Due</TableCell>
                <TableCell align="right">Charge</TableCell><TableCell align="right">Paid</TableCell><TableCell align="right">Open</TableCell><TableCell>Status</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {st.invoices.map((i: any) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.invoice_number}
                      {i.brought_forward ? <Typography variant="caption" color="text.secondary" display="block">invoice total {amt(i.invoice_total)} incl. {amt(i.brought_forward)} brought forward</Typography> : null}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(i.period_start)} – {day(i.period_end)}</TableCell>
                    <TableCell>{day(i.issued)}</TableCell><TableCell>{day(i.due_date)}</TableCell>
                    <TableCell align="right">{amt(i.charge)}</TableCell><TableCell align="right">{amt(i.paid) || '0.00'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{amt(i.open) || '0.00'}</TableCell>
                    <TableCell><Chip size="small" color={STATE[i.state]?.color} label={STATE[i.state]?.label + (i.state === 'paid' && i.paid_at ? ` ${day(i.paid_at)}` : i.days_overdue ? ` · ${i.days_overdue}d` : '')} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Payments and credits are applied to the oldest open invoice first; any excess is held as credit on account and used by the next invoice.
            Brought-forward lines on an invoice only show an earlier balance and are not charged again.
          </Typography>
        </>
      )}

      <Dialog open={!!pay} onClose={() => setPay(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{pay?.confirm ? 'Confirm payment received' : pay?.id ? 'Edit payment' : 'Record payment received'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField label={`Amount actually received (${cur})`} type="number" value={pay?.amount ?? ''} autoFocus
              onChange={e => setPay({ ...pay, amount: e.target.value })} helperText="Enter what reached the bank, even if it differs from the invoice." />
            <TextField label="Date received" type="date" InputLabelProps={{ shrink: true }} value={pay?.received_at || ''} onChange={e => setPay({ ...pay, received_at: e.target.value })} />
            <TextField select label="Method" value={pay?.method || 'bank_transfer'} onChange={e => setPay({ ...pay, method: e.target.value })}>
              {METHODS.map(m => <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>)}
            </TextField>
            <TextField label="Reference (bank ref / remittance)" value={pay?.reference || ''} onChange={e => setPay({ ...pay, reference: e.target.value })} />
            <TextField label={`Bank charges we absorb (${cur}, optional)`} type="number" value={pay?.bank_charges ?? ''} onChange={e => setPay({ ...pay, bank_charges: e.target.value })}
              helperText="Only if you accept the shortfall as settled; otherwise leave empty and it stays owed." />
            <TextField label="Notes" multiline minRows={2} value={pay?.notes || ''} onChange={e => setPay({ ...pay, notes: e.target.value })} />
            {st && pay && Number(pay.amount) > 0 && !pay.id && (
              <Alert severity="info" icon={false}>
                Balance after this payment: <b>{money(st.closing_balance - Number(pay.amount) - Number(pay.bank_charges || 0), cur)}</b>
                {st.closing_balance - Number(pay.amount) - Number(pay.bank_charges || 0) < 0 && ' (held as credit on account)'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPay(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePayment} disabled={busy || !(Number(pay?.amount) > 0) || !pay?.received_at}>{busy ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!adj} onClose={() => setAdj(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Account adjustment</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField select label="Type" value={adj?.kind || 'credit'} onChange={e => setAdj({ ...adj, kind: e.target.value })}>
              <MenuItem value="credit">Credit — reduce what the customer owes (write-off, discount, credit note)</MenuItem>
              <MenuItem value="debit">Debit — increase what the customer owes (extra charge)</MenuItem>
            </TextField>
            <TextField label={`Amount (${cur})`} type="number" value={adj?.amount ?? ''} onChange={e => setAdj({ ...adj, amount: e.target.value })} />
            <TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={adj?.adj_date || ''} onChange={e => setAdj({ ...adj, adj_date: e.target.value })} />
            <TextField label="Reason (shown on the statement)" value={adj?.reason || ''} onChange={e => setAdj({ ...adj, reason: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdj(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveAdj} disabled={busy || !(Number(adj?.amount) > 0) || !adj?.reason}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!voiding} onClose={() => setVoiding(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Void {voiding?.type}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>It stays in the history (marked void) but no longer counts on the account.</Typography>
          <TextField fullWidth label="Reason" value={voiding?.reason || ''} onChange={e => setVoiding({ ...voiding, reason: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoiding(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doVoid} disabled={busy || !voiding?.reason}>Void</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
